import React from 'react';
import { FileText, ListChecks, Stethoscope, PenLine, CalendarClock, ShieldCheck } from 'lucide-react';
import './HipaaFaq.css';

const certIconProps = { size: 20, color: 'var(--primary-color)' };

const phpCertificationSections = [
    {
        question: "What is the Initial Physician Certification?",
        icon: <FileText {...certIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>The Initial Physician Certification is a required attestation completed by a licensed physician at the time of admission to a Psychiatric Partial Hospitalization Program (PHP).</p>
                <p style={{ fontStyle: 'italic' }}>"I certify that this patient requires admission to a Psychiatric Partial Hospitalization Program (PHP) for the active treatment of an acute psychiatric condition."</p>
                <p><strong>Required patient identifiers:</strong> Patient Name, Date of Birth, MRN, and PHP Admission Date.</p>
            </div>
        )
    },
    {
        question: "What does the physician certify?",
        icon: <ListChecks {...certIconProps} />,
        answer: (
            <ol style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 10 }}>
                <li>The patient requires a comprehensive, structured, multimodal psychiatric treatment program under an individualized written plan of care and requires a minimum of 20 hours per week of therapeutic services.</li>
                <li>The patient's psychiatric condition severely interferes with multiple areas of daily functioning and requires the intensity, medical supervision, coordination, and multidisciplinary treatment provided by a PHP.</li>
                <li>The patient is able to cognitively and emotionally participate in active treatment and is capable of tolerating the intensity of the PHP.</li>
                <li>In the absence of partial hospitalization services, the patient would otherwise require inpatient psychiatric hospitalization or is at reasonable risk of requiring inpatient psychiatric hospitalization.</li>
                <li>The patient will remain under the care of a physician, and PHP services will be furnished pursuant to an individualized written plan of care established and periodically reviewed by the physician in consultation with the multidisciplinary treatment team.</li>
                <li>The PHP services prescribed are reasonable and necessary for treatment of the patient's psychiatric condition and are intended to reduce or control acute psychiatric symptoms, improve or maintain functioning, prevent deterioration, and facilitate treatment at the least restrictive clinically appropriate level of care.</li>
            </ol>
        )
    },
    {
        question: "What clinical basis must be documented?",
        icon: <Stethoscope {...certIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>The clinical basis must be individualized to the patient and support the acute psychiatric condition, functional impairment, need for PHP intensity, and risk of inpatient psychiatric hospitalization without PHP.</p>
                <p><strong>Primary psychiatric diagnosis</strong> must be recorded as part of the supporting clinical rationale.</p>
            </div>
        )
    },
    {
        question: "Who completes and signs the certification?",
        icon: <PenLine {...certIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>A physician (MD or DO) completes the certification and records their name, credentials, and signature, along with the date of certification.</p>
            </div>
        )
    },
    {
        question: "When is recertification required?",
        icon: <CalendarClock {...certIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Initial certification should be completed at admission.</p>
                <p>The <strong>first PHP recertification</strong> is required as of the 18th day of PHP services, with subsequent recertifications no less frequently than every 30 days.</p>
            </div>
        )
    },
    {
        question: "Compliance note",
        icon: <ShieldCheck {...certIconProps} />,
        answer: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>For Physicians Health Portal internal clinical/compliance use. This is for guidance only. Final decisions must be reviewed by the Medical Director or Compliance team. Regulations can change.</p>
            </div>
        )
    }
];

export default function PhysicianCertification() {
    return (
        <section className="hipaa-faq">
            <div className="page-heading">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{ padding: 12, backgroundColor: 'var(--primary-color)', borderRadius: 12, color: 'white' }}>
                        <Stethoscope size={32} />
                    </div>
                    <div>
                        <div className="eyebrow">CLINICAL COMPLIANCE</div>
                        <h1 style={{ margin: 0 }}>Physician Certification</h1>
                        <p style={{ margin: 0 }}>Initial physician certification requirements for the Psychiatric Partial Hospitalization Program (PHP).</p>
                    </div>
                </div>
            </div>

            <div className="hipaa-questions">
                {phpCertificationSections.map((s, i) => (
                    <details className="content-card" key={i} open={i === 0}>
                        <summary style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                            {s.icon}
                            <span>{s.question}</span>
                        </summary>
                        <div className="hipaa-answer" style={{ marginTop: 16, paddingBottom: 8 }}>
                            {s.answer}
                        </div>
                    </details>
                ))}
            </div>
        </section>
    );
}
